import {PRODUCT_SLUGS, ProductSlug} from '../../shared/products';

export interface PublicProductFeatureGroup {
    title: string;
    items: string[];
    sourceFiles: string[];
}

export interface PublicProductFeatureValueRow {
    feature: string;
    whatItDoes: string;
    whyItsValuable: string;
    sourceFiles: string[];
}

export interface PublicProductExclusion {
    label: string;
    status: 'internal' | 'planned' | 'blocked';
    reason: string;
}

export interface PublicProductPageContent {
    slug: ProductSlug;
    name: string;
    eyebrow: string;
    summary: string;
    audience: string;
    logoSrc: string;
    primaryRoute: string;
    featureGroups: PublicProductFeatureGroup[];
    featureValueRows: PublicProductFeatureValueRow[];
    workflows: string[];
    configSurfaces: string[];
    integrations: string[];
    constraints: string[];
    exclusions: PublicProductExclusion[];
    sourceFiles: string[];
}

export const PUBLIC_PRODUCT_FEATURE_TABLE_COLUMNS = ['Feature', 'What it does', 'Why its valuable'] as const;

const AI_SOURCE_FILES = [
    'shine/docs/user-manual/shine-ai-user-manual.md',
    'shine-ai/README.md',
    'shine-ai-frontend/README.md',
    'shine-ai-frontend/docs/ARCHITECTURE.md',
    'shine-ai-frontend/docs/GTM_SETUP.md',
    'shine-ai-frontend/docs/INTEGRATION_PATTERNS.md',
    'shine-ai-frontend/docs/PRODUCT_CARDS.md',
    'shine/src/app/components/chatbots/settings/chatbot-settings.component.html',
    'shine/src/app/components/chatbots/settings/frontend-settings/frontend-settings.component.html',
    'shine/src/app/components/chatbots/settings/messages-settings/messages-settings.component.html',
    'shine/src/app/components/dashboard/reports/overall-dashboard.components.ts',
    'shine/src/app/layouts/full/sidebar/sidebar-data.ts',
];

const CONNECT_SOURCE_FILES = [
    'shine-connect/README.md',
    'shine-connect/apps/api/README.md',
    'shine-connect/docs/backend/connections.md',
    'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
    'shine/src/app/components/connect/workflows/connect-workflows.component.html',
    'shine/src/app/components/connect/workflows/connect-runs.component.html',
    'shine/src/app/components/connect/connections/connect-connections.component.html',
    'shine/src/app/components/connect/settings/connect-settings.component.html',
    'shine/src/app/layouts/full/sidebar/sidebar-data.ts',
];

const ENRICH_SOURCE_FILES = [
    'shine/docs/user-manual/enrich-user-manual.md',
    'shine-enrich-core/README.md',
    'shine-enrich-brreg-sync/README.md',
    'shine/src/app/components/enrich/setup/setup.component.html',
    'shine/src/app/components/enrich/settings/settings.component.html',
    'shine/src/app/components/enrich/mapping/mapping.component.html',
    'shine/src/app/components/enrich/companies/companies.component.html',
    'shine/src/app/components/enrich/companies/company-detail.component.html',
    'shine/src/app/components/enrich/filters/filters.component.html',
    'shine/src/app/components/enrich/sync-history/sync-history.component.html',
    'shine/src/app/components/enrich/sync-history/sync-history-detail.component.html',
    'shine/src/app/layouts/full/sidebar/sidebar-data.ts',
];

const TRANSLATIONS_SOURCE_FILES = [
    'shine-translations/README.md',
    'shine-translations/src/Shine.Translations.Api/Program.cs',
    'shine-translations/src/Shine.Translations.Api/TranslationsEndpoints.cs',
    'shine-translations/src/Shine.Translations.Api/TranslationRequestValidation.cs',
    'shine-translations/src/Shine.Translations.Api.Tests/TranslationsEndpointContractTests.cs',
    'shine-translations/src/Shine.Translations.Api.Tests/TranslationPageSyncProducerTests.cs',
    'shine-translations/src/Shine.Translations.Api.Tests/HubSpotWebhookEndpointTests.cs',
    'shine/src/app/components/translations/translations-admin.component.html',
    'shine/src/app/components/translations/translations-admin.component.ts',
    'shine/src/app/layouts/full/sidebar/sidebar-data.ts',
];

const WEBINARS_SOURCE_FILES = [
    'shine-webinars/README.md',
    'shine-webinars/docs/hubspot-webhooks.md',
    'shine-webinars/docs/hubspot-app-object-webinar.md',
    'shine/src/app/components/webinars/webinars-admin.component.html',
    'shine/src/app/components/webinars/webinar-detail-page/webinar-detail-page.component.html',
    'shine/src/app/components/webinars/webinar-settings/webinar-settings.component.ts',
    'shine/src/app/components/webinars/webinar-analytics-panel/webinar-analytics-panel.component.html',
    'shine/src/app/layouts/full/sidebar/sidebar-data.ts',
];

const AI_FEATURE_VALUE_ROWS: PublicProductFeatureValueRow[] = [
    {
        feature: 'Customizable instructions',
        whatItDoes:
            'Stores assistant, chatbot, sanitizer, summary, and widget-text instructions in Shine backoffice and admin settings.',
        whyItsValuable:
            'Lets teams tune a company-specific assistant without rewriting code whenever behavior, tone, or handover text needs to change.',
        sourceFiles: [
            'shine/src/app/components/assistants/settings/assistant-general-settings/assistant-general-settings.component.html',
            'shine/src/app/components/chatbots/settings/chatbot-settings.component.html',
            'shine/src/app/components/chatbots/settings/messages-settings/messages-settings.component.html',
            'shine-ai/Admin/README.md',
        ],
    },
    {
        feature: 'GA4 Ecom',
        whatItDoes:
            'Emits GA4-standard product-card events for shown recommendations, selected items, and add-to-cart actions when ecommerce tracking is enabled.',
        whyItsValuable:
            'Shows whether chat-driven product discovery is creating product views, clicks, and basket actions in the same reporting flow as the storefront.',
        sourceFiles: [
            'shine-ai-frontend/docs/GTM_SETUP.md',
            'shine-ai-frontend/docs/PRODUCT_CARDS.md',
            'shine-ai-frontend/docs/TELEMETRY.md',
        ],
    },
    {
        feature: 'Shopping basket integration',
        whatItDoes:
            'Adds product-card Add to cart actions that call the host `window.shineAddToCart(product)` callback and dispatch a `shine:add-to-cart` event.',
        whyItsValuable:
            'Shortens the path from recommendation to basket by handing the selected product to the site cart flow without forcing the user out of chat.',
        sourceFiles: [
            'shine-ai-frontend/docs/PRODUCT_CARDS.md',
            'shine-ai-frontend/docs/GTM_SETUP.md',
            'shine-ai-frontend/src/__tests__/product-card-analytics-service.test.ts',
        ],
    },
    {
        feature: 'Product cards',
        whatItDoes:
            'Renders structured product, car, and no-price cards from chat output and stores saved product-card state in the widget.',
        whyItsValuable:
            'Turns AI answers into something customers can inspect and act on instead of giving them a plain text recommendation to chase manually.',
        sourceFiles: [
            'shine-ai-frontend/docs/ARCHITECTURE.md',
            'shine-ai-frontend/docs/PRODUCT_CARDS.md',
            'shine-ai-frontend/docs/COMPONENTS.md',
        ],
    },
    {
        feature: 'Product feed bindings',
        whatItDoes:
            'Links assistants to selected product feeds and lets one feed act as the default fallback for assistant product answers.',
        whyItsValuable:
            'Keeps product answers closer to catalog data the team already maintains in Shine instead of relying on generic answer text alone.',
        sourceFiles: [
            'shine/src/app/components/assistants/settings/assistant-settings/assistant-settings.component.html',
            'shine/src/app/components/product-feeds/product-feeds-settings/product-feed-general-settings/product-feed-general-settings.component.html',
            'shine/src/app/components/product-feeds/product-feeds-settings/product-feed-settings/product-feed-settings.component.html',
        ],
    },
    {
        feature: 'Draft validation and regression',
        whatItDoes:
            'Saves drafts, validates them, and runs stored regression checks before publish and rollback actions.',
        whyItsValuable:
            'Adds a governed review step before new assistant behavior reaches the live assistant.',
        sourceFiles: [
            'shine/src/app/components/assistants/settings/assistant-settings/assistant-settings.component.html',
            'shine/src/app/components/chatbots/settings/chatbot-settings.component.html',
            'shine-ai/Admin/Services/Assistants/AssistantVersionsController.cs',
            'shine-ai/Admin/Mcp/AdminMcpToolService.cs',
        ],
    },
    {
        feature: 'Knowledge files',
        whatItDoes:
            'Lets assistants use direct and vector-search file sets as reusable knowledge inputs, with optional brand and market scope for multi-brand assistants.',
        whyItsValuable:
            'Keeps approved company knowledge reusable without exposing brand- or market-specific guidance in the wrong multi-brand journey.',
        sourceFiles: [
            'shine/src/app/components/files/files-settings/file-settings/file-settings.component.html',
            'shine/src/app/components/assistants/settings/assistant-settings/assistant-settings.component.html',
            'shine-ai/Web/README.md',
        ],
    },
    {
        feature: 'Conversation review and reporting',
        whatItDoes:
            'Shows conversation transcripts, reporting views, and recent activity for operational review.',
        whyItsValuable:
            'Gives teams a concrete feedback loop for improving assistant answers, tasks, and product-card journeys over time.',
        sourceFiles: [
            'shine/docs/user-manual/shine-ai-user-manual.md',
            'shine/src/app/components/dashboard/reports/overall-dashboard.components.ts',
            'shine/src/app/components/conversations/conversations/conversations.component.html',
        ],
    },
];

const CONNECT_FEATURE_VALUE_ROWS: PublicProductFeatureValueRow[] = [
    {
        feature: 'Workflow canvas',
        whatItDoes:
            'Builds workflows from triggers, conditionals, loops, joins, waits, transforms, API calls, connector actions, and AI nodes.',
        whyItsValuable:
            'Keeps integration logic visible and editable in one place instead of spreading it across separate scripts and point-to-point fixes.',
        sourceFiles: [
            'shine-connect/apps/api/README.md',
            'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
            'shine/src/app/components/connect/workflows/connect-workflows.component.html',
        ],
    },
    {
        feature: 'Manual, webhook, and scheduled triggers',
        whatItDoes:
            'Starts workflows from user action, incoming HTTP events, or configured schedules.',
        whyItsValuable:
            'Supports both operator-run tasks and background automations without asking teams to adopt a separate trigger tool for each case.',
        sourceFiles: [
            'shine-connect/apps/api/README.md',
            'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
        ],
    },
    {
        feature: 'Connection catalog',
        whatItDoes:
            'Creates and manages OAuth2, API key, bearer, basic, and custom connections through reusable provider definitions.',
        whyItsValuable:
            'Lets teams connect a system once and reuse it across workflows instead of duplicating credentials per automation.',
        sourceFiles: [
            'shine-connect/docs/backend/connections.md',
            'shine/src/app/components/connect/connections/connect-connections.component.html',
            'shine/src/app/components/connect/settings/connect-settings.component.html',
        ],
    },
    {
        feature: 'HubSpot object actions',
        whatItDoes:
            'Models HubSpot companies, contacts, deals, owners, other objects, and custom objects as connector actions.',
        whyItsValuable:
            'Moves common CRM updates into repeatable workflow steps instead of leaving them as one-off record work.',
        sourceFiles: [
            'shine-connect/README.md',
            'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
        ],
    },
    {
        feature: 'Run history',
        whatItDoes:
            'Lists workflow runs with status, duration, start time, workflow filtering, inspection, and rerun actions.',
        whyItsValuable:
            'Gives operators a practical place to inspect what happened and retry the same flow when an integration needs follow-up.',
        sourceFiles: [
            'shine-connect/apps/api/README.md',
            'shine/src/app/components/connect/workflows/connect-runs.component.html',
        ],
    },
    {
        feature: 'AI gateway nodes',
        whatItDoes:
            'Routes AI chat, embedding, and reranker steps through the Connect gateway node set.',
        whyItsValuable:
            'Lets teams place AI steps inside the same workflow graph as the rest of the automation while keeping calls on the backend.',
        sourceFiles: [
            'shine-connect/apps/api/README.md',
            'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
        ],
    },
    {
        feature: 'Secret boundaries',
        whatItDoes:
            'Stores connection secrets server-side and resolves them through Shine Auth-backed infrastructure.',
        whyItsValuable:
            'Lets operators configure integrations without putting provider credentials into workflow JSON or browser state.',
        sourceFiles: [
            'shine-connect/docs/backend/connections.md',
            'shine-connect/apps/api/README.md',
        ],
    },
    {
        feature: 'Provider settings',
        whatItDoes:
            'Shows provider catalog entries, docs, region selection, and connection setup details.',
        whyItsValuable:
            'Speeds onboarding when a team needs to understand which providers exist and how each one should be configured before building workflows.',
        sourceFiles: [
            'shine/src/app/components/connect/settings/connect-settings.component.html',
            'shine/src/app/components/connect/settings/connect-provider-detail.component.html',
            'shine-connect/docs/backend/connections.md',
        ],
    },
];

const ENRICH_FEATURE_VALUE_ROWS: PublicProductFeatureValueRow[] = [
    {
        feature: 'HubSpot readiness setup',
        whatItDoes:
            'Checks authentication, property setup, webhook setup, and readiness before operational Enrich pages are enabled.',
        whyItsValuable:
            'Gets the CRM connection, property model, and update hooks in place before teams start importing or syncing company data.',
        sourceFiles: [
            'shine/docs/user-manual/enrich-user-manual.md',
            'shine/src/app/components/enrich/setup/setup.component.html',
            'shine/src/app/components/enrich/settings/settings.component.html',
        ],
    },
    {
        feature: 'Field mapping',
        whatItDoes:
            'Maps registry-backed fields to HubSpot company properties with overwrite controls.',
        whyItsValuable:
            'Lets each CRM team decide where public-company data should land and which existing properties must stay protected.',
        sourceFiles: [
            'shine/docs/user-manual/enrich-user-manual.md',
            'shine/src/app/components/enrich/mapping/mapping.component.html',
            'shine/src/app/components/enrich/settings/settings.component.html',
        ],
    },
    {
        feature: 'Company search',
        whatItDoes:
            'Searches companies by name, organization number, postal code, NACE, employee group, city, region, and organization type.',
        whyItsValuable:
            'Reduces manual company lookup when teams need reliable public-company context before importing or updating CRM records.',
        sourceFiles: [
            'shine/docs/user-manual/enrich-user-manual.md',
            'shine/src/app/components/enrich/companies/companies.component.html',
            'shine/src/app/components/enrich/companies/company-detail.component.html',
        ],
    },
    {
        feature: 'Company import',
        whatItDoes:
            'Imports individual or selected company records from search results into HubSpot.',
        whyItsValuable:
            'Moves selected registry records into HubSpot without retyping the same company details by hand.',
        sourceFiles: [
            'shine/docs/user-manual/enrich-user-manual.md',
            'shine/src/app/components/enrich/companies/companies.component.html',
        ],
    },
    {
        feature: 'Import filters',
        whatItDoes:
            'Creates, edits, enables, disables, runs, and deletes saved import filters.',
        whyItsValuable:
            'Turns repeated prospecting or refresh criteria into reusable enrichment runs instead of rebuilding the same search each time.',
        sourceFiles: [
            'shine/docs/user-manual/enrich-user-manual.md',
            'shine/src/app/components/enrich/filters/filters.component.html',
            'shine/src/app/components/enrich/filters/filter-form.component.html',
        ],
    },
    {
        feature: 'Sync history',
        whatItDoes:
            'Filters sync events by company, status, type, and date, then opens detail for individual changes.',
        whyItsValuable:
            'Shows which company records changed, when they changed, and which syncs need operator follow-up.',
        sourceFiles: [
            'shine/src/app/components/enrich/sync-history/sync-history.component.html',
            'shine/src/app/components/enrich/sync-history/sync-history-detail.component.html',
            'shine-enrich-core/README.md',
        ],
    },
    {
        feature: 'Registry provider selection',
        whatItDoes:
            'Uses configured registry providers and country-aware lookup paths for company enrichment.',
        whyItsValuable:
            'Keeps the data source explicit when enrichment needs to use more than one registry-backed provider.',
        sourceFiles: [
            'shine-enrich-core/README.md',
            'shine/src/app/components/enrich/settings/settings.component.html',
            'shine-enrich-core/src/services/registry/providerSelection.ts',
        ],
    },
    {
        feature: 'Property scope controls',
        whatItDoes:
            'Limits which HubSpot properties are read or updated for an active connection profile.',
        whyItsValuable:
            'Reduces accidental CRM writes outside the approved property set for enrichment.',
        sourceFiles: [
            'shine/src/app/components/enrich/settings/settings.component.html',
            'shine-enrich-core/src/services/sync/CompanySyncService.ts',
            'shine-enrich-core/src/services/hubspot/HubspotCompanyService.ts',
        ],
    },
];

const TRANSLATIONS_FEATURE_VALUE_ROWS: PublicProductFeatureValueRow[] = [
    {
        feature: 'HubSpot page listing',
        whatItDoes:
            'Lists HubSpot CMS pages by connection and content type for translation operations.',
        whyItsValuable:
            'Gives editors a scoped page inventory before they queue any translation work.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine/src/app/components/translations/translations-admin.component.html',
            'shine/src/app/components/translations/translations-admin.component.ts',
        ],
    },
    {
        feature: 'Translation jobs',
        whatItDoes:
            'Creates translation jobs with target language, content type, source page, and optional instructions.',
        whyItsValuable:
            'Turns recurring translation requests into trackable work instead of copy-paste translation tasks.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine-translations/src/Shine.Translations.Api/TranslationsEndpoints.cs',
            'shine/src/app/components/translations/translations-admin.component.html',
        ],
    },
    {
        feature: 'Auto-sync settings',
        whatItDoes:
            'Configures include, exclude, or off behavior per content type and selected auto-translate languages.',
        whyItsValuable:
            'Keeps automatic translation focused on the languages and page types a team actually maintains.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine/src/app/components/translations/translations-admin.component.html',
            'shine/src/app/components/translations/translations-admin.component.ts',
        ],
    },
    {
        feature: 'Form and CTA policy',
        whatItDoes:
            'Stores policy choices for forms and CTAs alongside model and instruction settings.',
        whyItsValuable:
            'Makes conversion-sensitive elements explicit when translated pages need controlled handling instead of silent rewrites.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine/src/app/components/translations/translations-admin.component.html',
            'shine-translations/src/Shine.Translations.Api/TranslationRequestValidation.cs',
        ],
    },
    {
        feature: 'Draft variants',
        whatItDoes:
            'Creates or reuses HubSpot language variants as draft output instead of publishing automatically.',
        whyItsValuable:
            'Keeps human review and publishing control with the site team before translated pages go live.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine-translations/src/Shine.Translations.Api/TranslationsEndpoints.cs',
            'shine-translations/src/Shine.Translations.Api.Tests/TranslationsEndpointContractTests.cs',
        ],
    },
    {
        feature: 'Structure preservation',
        whatItDoes:
            'Preserves HubL, HTML, URLs, forms, module structure, and non-translatable tokens during translation.',
        whyItsValuable:
            'Protects page structure and tracking details while routine CMS content is localized.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine-translations/src/Shine.Translations.Api/TranslationsEndpoints.cs',
        ],
    },
    {
        feature: 'History and out-of-sync review',
        whatItDoes:
            'Tracks changed source content, external edits, and job state for existing translated variants.',
        whyItsValuable:
            'Shows editors which translated variants need another pass after the source page changes.',
        sourceFiles: [
            'shine-translations/README.md',
            'shine/src/app/components/translations/translations-admin.component.html',
        ],
    },
    {
        feature: 'Validation and sync boundaries',
        whatItDoes:
            'Validates HubSpot webhook intake, sync requests, and tenant identity on the server before translation work is queued.',
        whyItsValuable:
            'Keeps automatic translation events tied to the right client instead of trusting unsigned or mixed client input.',
        sourceFiles: [
            'shine-translations/src/Shine.Translations.Api.Tests/HubSpotWebhookEndpointTests.cs',
            'shine-translations/src/Shine.Translations.Api/TranslationRequestValidation.cs',
            'shine-translations/src/Shine.Translations.Api/Program.cs',
            'shine-translations/README.md',
        ],
    },
];

const WEBINARS_FEATURE_VALUE_ROWS: PublicProductFeatureValueRow[] = [
    {
        feature: 'Webinar setup and admin',
        whatItDoes:
            'Creates, updates, deletes, previews, and configures webinars from authenticated admin surfaces.',
        whyItsValuable:
            'Gives teams one operational place to prepare and maintain webinars before sending traffic to the public pages.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine/src/app/components/webinars/webinars-admin.component.html',
            'shine/src/app/components/webinars/webinar-detail-page/webinar-detail-page.component.html',
        ],
    },
    {
        feature: 'Attendee access flow',
        whatItDoes:
            'Serves public webinar listings, per-webinar login, denied-access redirects, and signed attendee cookies for viewer state.',
        whyItsValuable:
            'Lets attendees reach the right webinar page and keep access state without requiring a server-side session for every viewer.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine-webinars/views/home.html',
            'shine-webinars/views/login.html',
            'shine-webinars/views/webinar.html',
        ],
    },
    {
        feature: 'Branding and access settings',
        whatItDoes:
            'Configures logo, favicon, access mode, listing visibility, host password, and schedule fields for webinar pages.',
        whyItsValuable:
            'Lets teams prepare branded webinar pages while keeping access rules and visibility explicit before promotion starts.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine/src/app/components/webinars/webinar-settings/webinar-settings.component.ts',
            'shine/src/app/components/webinars/webinar-settings/webinar-settings.component.html',
        ],
    },
    {
        feature: 'Video playback',
        whatItDoes:
            'Embeds YouTube, Vimeo, or Dacast playback on webinar viewer pages.',
        whyItsValuable:
            'Allows webinar teams to keep the attendee video experience inside Shine while using the video provider they already selected.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine-webinars/views/webinar.html',
        ],
    },
    {
        feature: 'Live chat and replay',
        whatItDoes:
            'Supports Socket.IO chat with threading, reactions, replay, and speaker badge behavior.',
        whyItsValuable:
            'Keeps audience interaction visible during the live session and readable afterward for follow-up review.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine-webinars/docs/hubspot-webhooks.md',
            'shine/src/app/components/webinars/webinar-analytics-panel/webinar-analytics-panel.component.html',
        ],
    },
    {
        feature: 'Registration and attendance',
        whatItDoes:
            'Stores registrations separately, keeps attendance snapshots, and finalizes attendance duration after the webinar ends.',
        whyItsValuable:
            'Keeps registration and attendance records available for post-event follow-up instead of leaving webinar participation as a black box.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine-webinars/docs/hubspot-webhooks.md',
        ],
    },
    {
        feature: 'HubSpot Marketing Events sync',
        whatItDoes:
            'Syncs webinar events, registration properties, and completed attendance data to HubSpot where configured.',
        whyItsValuable:
            'Keeps webinar promotion and follow-up tied to HubSpot marketing operations instead of leaving the event isolated from CRM workflows.',
        sourceFiles: [
            'shine-webinars/README.md',
            'shine-webinars/docs/hubspot-app-object-webinar.md',
            'shine-webinars/docs/hubspot-webhooks.md',
        ],
    },
    {
        feature: 'Analytics and export',
        whatItDoes:
            'Shows webinar analytics and attendee-level activity in the admin panel and exports registrations for follow-up work.',
        whyItsValuable:
            'Gives teams a practical read on engagement after the event and an export path for downstream follow-up.',
        sourceFiles: [
            'shine/src/app/components/webinars/webinar-analytics-panel/webinar-analytics-panel.component.html',
            'shine/src/app/components/webinars/webinars-admin.component.html',
            'shine-webinars/README.md',
        ],
    },
];

export const PUBLIC_PRODUCT_PAGES: Record<ProductSlug, PublicProductPageContent> = {
    ai: {
        slug: 'ai',
        name: 'Shine AI',
        eyebrow: 'Conversational automation',
        summary:
            'Configure a company-specific AI assistant in Shine, connect it to product feeds and knowledge files, publish it in chat, and review how conversations perform from the same platform.',
        audience:
            'For marketers and teams that want a company-specific chatbot with governed instructions, product discovery, and measurable conversation review.',
        logoSrc: '/assets/images/logos/Shine%20AI.svg',
        primaryRoute: '/ai/dashboard',
        featureGroups: [
            {
                title: 'Chat agents and runtime',
                items: [
                    'Chatbots and assistants with configurable behavior, instructions, and knowledge sources',
                    'Conversations view for transcript review and performance analysis',
                    'SignalR streaming with pre-function, wait, and post-function message phases',
                    'Widget chat, inline chat, and product-card rendering through the frontend bundle',
                ],
                sourceFiles: [
                    'shine/docs/user-manual/shine-ai-user-manual.md',
                    'shine-ai/README.md',
                    'shine-ai-frontend/README.md',
                    'shine-ai-frontend/docs/ARCHITECTURE.md',
                    'shine/src/app/components/dashboard/reports/overall-dashboard.components.ts',
                ],
            },
            {
                title: 'Assistant and chatbot controls',
                items: [
                    'Assistant instructions, model selection, reasoning effort, summary verbosity, and temperature',
                    'Prompt cache, web search, code interpreter, and real-time voice toggles',
                    'Chatbot base URL, conversation retention override, sanitization instructions, and summary prompts',
                    'Widget launcher, chat window, localization, and HubSpot handover settings',
                ],
                sourceFiles: [
                    'shine/src/app/components/assistants/settings/assistant-general-settings/assistant-general-settings.component.html',
                    'shine/src/app/components/chatbots/settings/chatbot-settings.component.html',
                    'shine/src/app/components/chatbots/settings/frontend-settings/frontend-settings.component.html',
                    'shine/src/app/components/chatbots/settings/widget-settings/widget-settings.component.html',
                    'shine/src/app/components/chatbots/settings/messages-settings/messages-settings.component.html',
                ],
            },
            {
                title: 'Knowledge and data',
                items: [
                    'Direct file search and vector-search file sets',
                    'Product feeds for product-card and fallback product data',
                    'Functions and workflow-linked tool definitions',
                    'Server-owned conversation and provider conversation identifiers',
                ],
                sourceFiles: [
                    'shine/src/app/components/assistants/settings/assistant-settings/assistant-settings.component.html',
                    'shine/src/app/components/files/files-settings/file-settings/file-settings.component.html',
                    'shine/src/app/components/product-feeds/product-feeds-settings/product-feed-settings/product-feed-settings.component.html',
                    'shine-ai/Web/README.md',
                    'shine-ai/README.md',
                ],
            },
            {
                title: 'Automation and review',
                items: [
                    'Tasks for recurring activities, assignment, notifications, and completion tracking',
                    'Workflows for triggers, conditional logic, and execution history',
                    'Dashboard KPIs, recent activity, and reporting panels',
                    'Draft validation, regression tests, publishing, and rollback for assistant versions',
                ],
                sourceFiles: [
                    'shine/docs/user-manual/shine-ai-user-manual.md',
                    'shine/src/app/components/tasks/settings/task-settings/task-settings.component.html',
                    'shine/src/app/components/workflows/settings/workflow-settings/workflow-settings.component.html',
                    'shine/src/app/components/dashboard/reports/overall-dashboard.components.ts',
                    'shine/src/app/components/assistants/settings/assistant-settings/assistant-settings.component.html',
                ],
            },
        ],
        featureValueRows: AI_FEATURE_VALUE_ROWS,
        workflows: [
            'Configure chatbots, assistants, files, functions, and product feeds in the backoffice.',
            'Publish embedded widget or inline chat experiences for customer-facing sites.',
            'Review conversations, task runs, and reporting to improve automation quality over time.',
            'Save drafts, run regression tests, and publish assistant versions only after validation passes.',
        ],
        configSurfaces: [
            'Dashboard',
            'Chatbots',
            'Assistants',
            'Conversations',
            'Tasks',
            'Workflows',
            'Product Feeds',
            'Functions',
            'Files',
            'Widget Launcher',
            'Chat Window',
            'Integrations',
            'Publishing',
            'Regression tests',
            'Version history',
            'Analytics and reporting',
        ],
        integrations: [
            'Shine AI backend APIs',
            'SignalR runtime messaging',
            'OpenAI through backend services',
            'Shine AI frontend widget and inline chat',
            'Product-card renderers inside the frontend bundle',
        ],
        constraints: [
            'No direct browser LLM calls; the frontend routes AI requests through backend services.',
            'Runtime keys and provider conversation identifiers stay server-side.',
            'Assistant and chatbot publishing use draft, validation, and regression-test gates.',
            'Local debug fixtures exist for the UI shell but are not part of the public product claim.',
        ],
        exclusions: [
            {
                label: 'Admin MCP and debug fixtures',
                status: 'internal',
                reason: 'Backoffice automation and local inspection tooling are not public product claims.',
            },
            {
                label: 'Direct browser LLM execution',
                status: 'blocked',
                reason: 'All runtime calls are mediated through backend services.',
            },
        ],
        sourceFiles: AI_SOURCE_FILES,
    },
    connect: {
        slug: 'connect',
        name: 'Shine Connect',
        eyebrow: 'Workflow control plane',
        summary:
            'Build and run connected workflows across HubSpot, APIs, and AI steps from one control plane with reusable connections and inspectable runs.',
        audience:
            'For operations and integration teams that need simpler day-to-day connectivity between systems without scattering secrets or workflow logic.',
        logoSrc: '/assets/images/logos/Shine%20Connect.svg',
        primaryRoute: '/connect/workflows',
        featureGroups: [
            {
                title: 'Workflow builder',
                items: [
                    'Manual, webhook, and scheduled triggers',
                    'Condition, loop, wait, transform, join, and parallel logic nodes',
                    'API request and connector action nodes for external systems',
                    'AI chat, embedding, and reranker nodes through the gateway',
                ],
                sourceFiles: [
                    'shine-connect/apps/api/README.md',
                    'shine-connect/docs/connectors/implementing-nodes.md',
                    'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
                    'shine/src/app/components/connect/workflows/connect-workflows.component.html',
                ],
            },
            {
                title: 'Connection and provider management',
                items: [
                    'Create, edit, re-authenticate, and delete connections',
                    'OAuth2, API key, bearer, basic, and custom auth types',
                    'Provider catalog browsing with docs and region selection',
                    'Shine Auth-backed secret resolution and server-side masking',
                ],
                sourceFiles: [
                    'shine-connect/docs/backend/connections.md',
                    'shine-connect/apps/api/README.md',
                    'shine/src/app/components/connect/connections/connect-connections.component.html',
                    'shine/src/app/components/connect/settings/connect-settings.component.html',
                ],
            },
            {
                title: 'Run inspection and replay',
                items: [
                    'Run history with filter-by-workflow support',
                    'Open, inspect, and rerun workflow executions',
                    'Deterministic replay summaries for debugging and verification',
                    'Execution status, duration, and start time in the run table',
                ],
                sourceFiles: [
                    'shine-connect/apps/api/README.md',
                    'shine/src/app/components/connect/workflows/connect-runs.component.html',
                    'shine/src/app/components/connect/workflows/connect-workflows.component.html',
                ],
            },
            {
                title: 'HubSpot and data boundaries',
                items: [
                    'HubSpot companies, contacts, deals, owners, other objects, and custom objects',
                    'HTTP APIs and AI gateway integration for workflow steps',
                    'Redact-then-route policy for sensitive data and log hygiene',
                    'Connections keep browser-handled secrets out of the client',
                ],
                sourceFiles: [
                    'shine-connect/README.md',
                    'shine-connect/docs/backend/connections.md',
                    'shine/src/app/components/connect/workflows/connect-workflow-node-manifest.ts',
                    'shine/src/app/layouts/full/sidebar/sidebar-data.ts',
                ],
            },
        ],
        featureValueRows: CONNECT_FEATURE_VALUE_ROWS,
        workflows: [
            'Create a workflow from manual, scheduled, or webhook triggers.',
            'Add logic, connector, API, AI, and transform steps to model the process.',
            'Run, inspect, and iterate with replayable execution history.',
            'Use reusable Connections rather than duplicating secrets per workflow.',
        ],
        configSurfaces: [
            'Workflows',
            'Runs',
            'Connections',
            'Providers catalog',
            'Settings',
            'Auth callback',
            'Provider detail',
            'Connection detail',
        ],
        integrations: [
            'HubSpot CRM objects',
            'HTTP APIs',
            'AI gateway',
            'Shine Auth secret resolution',
        ],
        constraints: [
            'Secrets are managed through server-backed Connections and stay out of the browser.',
            'Raw PII must stay out of logs and traces.',
            'Provider settings and catalog edits are internal-admin surfaces.',
            'Replay and rerun are available for run inspection, not as a public marketing claim.',
        ],
        exclusions: [
            {
                label: 'Internal AI-function export/invoke boundary',
                status: 'internal',
                reason: 'This is a server-side control-plane boundary, not a customer-facing public feature.',
            },
            {
                label: 'Provider settings administration',
                status: 'internal',
                reason: 'Provider catalog editing is reserved for internal admin surfaces.',
            },
        ],
        sourceFiles: CONNECT_SOURCE_FILES,
    },
    enrich: {
        slug: 'enrich',
        name: 'Shine Enrich',
        eyebrow: 'Company data enrichment',
        summary:
            'Prepare HubSpot, search registry-backed company data, import the records you need, and keep mapped company properties aligned with public-source updates.',
        audience:
            'For CRM managers and sales, marketing, or support teams that need simpler company-data enrichment from public registry sources.',
        logoSrc: '/assets/images/logos/Shine%20Enrich.svg',
        primaryRoute: '/enrich/setup',
        featureGroups: [
            {
                title: 'Setup and readiness',
                items: [
                    'Authenticate with HubSpot and provision the required properties',
                    'Setup webhooks and confirm readiness before using operational pages',
                    'Configure CRM organization-number and subunit fields',
                    'Limit connection property scope to specific HubSpot properties',
                ],
                sourceFiles: [
                    'shine/docs/user-manual/enrich-user-manual.md',
                    'shine/src/app/components/enrich/setup/setup.component.html',
                    'shine/src/app/components/enrich/settings/settings.component.html',
                    'shine/src/app/components/enrich/mapping/mapping.component.html',
                ],
            },
            {
                title: 'Search and company detail',
                items: [
                    'Search by company name, organization number, postal code, NACE code, employee group, city, region, and organization type',
                    'Use registration and numeric filters for financial and registry criteria',
                    'Open company detail for address, website, phone, founding date, and registry-backed enrichment fields',
                    'Import individual companies or bulk-select from search results',
                ],
                sourceFiles: [
                    'shine/docs/user-manual/enrich-user-manual.md',
                    'shine/src/app/components/enrich/companies/companies.component.html',
                    'shine/src/app/components/enrich/companies/company-detail.component.html',
                ],
            },
            {
                title: 'Imports and filters',
                items: [
                    'Create, edit, enable, disable, run, and delete import filters',
                    'Set up manual and automated import paths with scheduling',
                    'Choose duplicate-handling and conflict-resolution behavior',
                    'Map registry fields to HubSpot properties and protect fields from overwrite',
                ],
                sourceFiles: [
                    'shine/docs/user-manual/enrich-user-manual.md',
                    'shine/src/app/components/enrich/filters/filters.component.html',
                    'shine/src/app/components/enrich/filters/filter-form.component.html',
                    'shine/src/app/components/enrich/settings/settings.component.html',
                ],
            },
            {
                title: 'Sync history and registry sources',
                items: [
                    'Inspect sync history by company, status, type, and date range',
                    'Open change details for an individual sync event',
                    'Select registry providers and keep refresh summaries visible in settings',
                    'Use BRREG-backed data and shared registry providers where configured',
                ],
                sourceFiles: [
                    'shine-enrich-core/README.md',
                    'shine-enrich-brreg-sync/README.md',
                    'shine/src/app/components/enrich/sync-history/sync-history.component.html',
                    'shine/src/app/components/enrich/sync-history/sync-history-detail.component.html',
                    'shine/src/app/components/enrich/settings/settings.component.html',
                ],
            },
        ],
        featureValueRows: ENRICH_FEATURE_VALUE_ROWS,
        workflows: [
            'Connect HubSpot and complete the required readiness checks.',
            'Map CRM fields to registry-backed company data.',
            'Search, filter, import, and monitor sync history.',
            'Use setup, settings, mapping, and sync history before opening operational pages.',
        ],
        configSurfaces: [
            'Setup',
            'Settings',
            'Field Mapping',
            'Search',
            'Imports',
            'Sync History',
            'Company detail',
            'Registry provider selection',
            'Property scope',
        ],
        integrations: [
            'HubSpot CRM',
            'Norwegian Business Registry data',
            'Shine Enrich Core',
            'BRREG sync service',
            'Shine Auth',
        ],
        constraints: [
            'Search, imports, and sync history are readiness-gated behind setup.',
            'Organization number is the canonical matching key for Enrich workflows.',
            'Logs and traces must avoid raw personal data.',
            'Property creation and scope controls must exist before operational pages are enabled.',
        ],
        exclusions: [
            {
                label: 'Non-HubSpot parity on the public page',
                status: 'planned',
                reason:
                    'The public backoffice surfaces in this repo are HubSpot-backed; broader CRM parity is not evidenced here.',
            },
        ],
        sourceFiles: ENRICH_SOURCE_FILES,
    },
    translations: {
        slug: 'translations',
        name: 'Shine Translations',
        eyebrow: 'HubSpot CMS localization',
        summary:
            'List HubSpot pages, queue translation jobs, keep selected content in sync, and write draft language variants that editors can review before publishing.',
        audience:
            'For CMS editors and content teams that need a repeatable way to translate routine HubSpot pages without manual page-by-page handling.',
        logoSrc: '/assets/images/logos/Shine%20logo.svg',
        primaryRoute: '/translations',
        featureGroups: [
            {
                title: 'Page and job operations',
                items: [
                    'List HubSpot CMS pages by connection and content type',
                    'Create translation jobs with target language and optional per-job instructions',
                    'View job history, review scores, and translation state',
                    'Retranslate individual variations when source content changes',
                ],
                sourceFiles: [
                    'shine-translations/README.md',
                    'shine/src/app/components/translations/translations-admin.component.html',
                    'shine/src/app/components/translations/translations-admin.component.ts',
                ],
            },
            {
                title: 'Auto-sync and policy controls',
                items: [
                    'Configure sync connection and include/exclude/off modes per content type',
                    'Manage auto-translate languages per client',
                    'Set default instructions, default model, form policy, and CTA policy',
                    'Run sync manually for all eligible pages or a single page',
                ],
                sourceFiles: [
                    'shine-translations/README.md',
                    'shine/src/app/components/translations/translations-admin.component.html',
                    'shine/src/app/components/translations/translations-admin.component.ts',
                ],
            },
            {
                title: 'Content preservation and review',
                items: [
                    'Preserve HubL, HTML, URLs, forms, and module structure during translation',
                    'Create or reuse language variants in HubSpot automatically',
                    'Track out-of-sync variants and external edits',
                    'Keep translation jobs bounded by server-side validation and token limits',
                ],
                sourceFiles: [
                    'shine-translations/README.md',
                    'shine-translations/src/Shine.Translations.Api/TranslationsEndpoints.cs',
                    'shine-translations/src/Shine.Translations.Api/TranslationRequestValidation.cs',
                    'shine/src/app/components/translations/translations-admin.component.html',
                ],
            },
            {
                title: 'Validation boundaries',
                items: [
                    'Signed HubSpot webhook intake binds tenant identity server-side',
                    'Connection and client identity resolve through Shine Auth',
                    'Page extraction, sync, and translation are validated before work is queued',
                    'Draft variants are written without auto-publish',
                ],
                sourceFiles: [
                    'shine-translations/README.md',
                    'shine-translations/docs/hubspot-webhook-intake.md',
                    'shine-translations/src/Shine.Translations.Api.Tests/HubSpotWebhookEndpointTests.cs',
                    'shine-translations/src/Shine.Translations.Api.Tests/TranslationsEndpointContractTests.cs',
                ],
            },
        ],
        featureValueRows: TRANSLATIONS_FEATURE_VALUE_ROWS,
        workflows: [
            'Choose a HubSpot connection, content type, and target language.',
            'Create a translation job or configure auto-sync rules.',
            'Review the resulting draft variant in HubSpot before publishing.',
            'Retranslate variants and inspect job history when source content changes.',
        ],
        configSurfaces: [
            'Pages',
            'Auto-sync',
            'Settings',
            'Jobs',
            'Job detail',
            'Translation variants',
            'Connection selector',
        ],
        integrations: [
            'HubSpot CMS pages',
            'Shine Auth',
            'Shine Connect connections',
            'OpenAI Responses through backend services',
        ],
        constraints: [
            'Translation updates are draft-only; there is no auto-publish path.',
            'Content type, languages, model names, instruction length, and policy values are validated server-side.',
            'Raw HubSpot content, prompts, tokens, and translated text are not stored in sync-event records.',
            'Target segment counts are capped at 200 per job.',
        ],
        exclusions: [
            {
                label: 'Auto-publish of translated pages',
                status: 'blocked',
                reason: 'The service writes draft variants only.',
            },
            {
                label: 'Debug extraction targets outside local/dev',
                status: 'internal',
                reason: 'Debug targets are explicitly hidden from beta/staging/production.',
            },
            {
                label: 'Raw prompt or content dumps',
                status: 'blocked',
                reason: 'The service keeps job records and sync events compact and sanitized.',
            },
        ],
        sourceFiles: TRANSLATIONS_SOURCE_FILES,
    },
    webinars: {
        slug: 'webinars',
        name: 'Shine Webinars',
        eyebrow: 'Webinar operations',
        summary:
            'Run branded webinar pages, attendee access, live chat, registrations, and HubSpot-connected follow-up from one webinar workflow.',
        audience:
            'For teams hosting webinars that need HubSpot-connected registrations, attendee access, engagement, and post-event follow-up.',
        logoSrc: '/assets/images/logos/Shine%20logo.svg',
        primaryRoute: '/webinars',
        featureGroups: [
            {
                title: 'Public attendee flow',
                items: [
                    'Public webinar listings and per-webinar login flows',
                    'Tenant routing through custom domains or the /t/:clientId fallback',
                    'Signed attendee cookies for access state without a server session',
                    'Viewer pages for embedded playback and attendee interaction',
                ],
                sourceFiles: [
                    'shine-webinars/README.md',
                    'shine-webinars/views/home.html',
                    'shine-webinars/views/login.html',
                    'shine-webinars/views/webinar.html',
                ],
            },
            {
                title: 'Live engagement',
                items: [
                    'YouTube, Vimeo, and Dacast playback',
                    'Socket.IO chat with message threading, reactions, and replay',
                    'Speaker badge and moderation behavior for organizers',
                    'Attendance tracking and engagement state across reconnects',
                ],
                sourceFiles: [
                    'shine-webinars/README.md',
                    'shine-webinars/docs/hubspot-webhooks.md',
                    'shine/src/app/components/webinars/webinars-detail/webinars-detail.component.html',
                    'shine/src/app/components/webinars/webinar-analytics-panel/webinar-analytics-panel.component.html',
                ],
            },
            {
                title: 'Admin operations',
                items: [
                    'Create, update, delete, and preview webinars from the backoffice',
                    'Export attendee registrations and review chat transcripts',
                    'Publish and close polls or resource engagements',
                    'Inspect webinar analytics and change-level engagement details',
                ],
                sourceFiles: [
                    'shine-webinars/README.md',
                    'shine/src/app/components/webinars/webinar-detail-page/webinar-detail-page.component.html',
                    'shine/src/app/components/webinars/webinar-settings/webinar-settings.component.ts',
                    'shine/src/app/components/webinars/webinars-admin.component.html',
                ],
            },
            {
                title: 'Settings and HubSpot sync',
                items: [
                    'Branding logo and favicon settings',
                    'HubSpot Marketing Events sync and registration-property provisioning',
                    'Access mode, listing visibility, host password, and schedule controls',
                    'Per-webinar settings backed by authenticated admin routes',
                ],
                sourceFiles: [
                    'shine-webinars/README.md',
                    'shine-webinars/docs/hubspot-app-object-webinar.md',
                    'shine/src/app/components/webinars/webinar-settings/webinar-settings.component.ts',
                    'shine/src/app/components/webinars/webinar-settings/webinar-settings.component.html',
                ],
            },
        ],
        featureValueRows: WEBINARS_FEATURE_VALUE_ROWS,
        workflows: [
            'Create or update webinar details in the Shine backoffice.',
            'Publish the public webinar page and collect registrations through the configured flow.',
            'Run the live event, review engagement, and export or sync follow-up data.',
            'Use webinar settings to provision HubSpot tracking and branding before promotion.',
        ],
        configSurfaces: [
            'Webinars list',
            'Webinar detail',
            'Webinar settings',
            'Analytics panel',
            'Registrations export',
            'Chat review',
            'Engagement editor',
            'Preview flow',
        ],
        integrations: [
            'YouTube, Vimeo, or Dacast playback',
            'Socket.IO chat',
            'HubSpot Marketing Events',
            'Cosmos-backed webinar storage',
            'Shine Auth tokens or API keys for admin routes',
        ],
        constraints: [
            'Attendee sessions use signed HTTP-only cookies.',
            'Resource engagements are URL-only today; binary upload/storage is not implemented.',
            'Staging hard-disables HubSpot background workers.',
            'Admin APIs remain guarded by Shine Auth tokens or API keys.',
        ],
        exclusions: [
            {
                label: 'Binary resource uploads or storage',
                status: 'blocked',
                reason: 'Resources are URL-only in the current service.',
            },
            {
                label: 'Staging HubSpot worker processing',
                status: 'internal',
                reason: 'Background HubSpot workers are statically disabled in staging.',
            },
        ],
        sourceFiles: WEBINARS_SOURCE_FILES,
    },
};

export const PUBLIC_PRODUCT_SLUGS = Object.values(PRODUCT_SLUGS);
export const PUBLIC_PRODUCT_NAV_ITEMS = Object.values(PUBLIC_PRODUCT_PAGES);

export function getPublicProductPage(slug?: string | null): PublicProductPageContent | null {
    if (!slug) {
        return null;
    }

    const normalizedSlug = slug.toLowerCase() as ProductSlug;
    return PUBLIC_PRODUCT_PAGES[normalizedSlug] ?? null;
}

export function isPublicProductPageUrl(url: string): boolean {
    const parsedUrl = new URL(url, 'http://shine.local');
    const normalizedPath = parsedUrl.pathname.replace(/\/+$/, '') || '/';

    if (normalizedPath === '/products') {
        return true;
    }

    const match = normalizedPath.match(/^\/products\/([^/]+)$/);
    return Boolean(match && getPublicProductPage(match[1]));
}
